/* -----------------------------------------------------------------------------
 * UI string dictionary + language registry.
 *
 * The brief requires multilingual support ("visitors select their own native
 * language"). The architecture here supports translations for every UI string
 * and every blog post (posts carry their own translations server-side). English
 * is the complete source; a few key languages are populated as a demonstration,
 * and any missing key falls back to English so nothing ever renders blank.
 *
 * Adding a language = add it to LANGUAGES and drop a block into `strings`.
 * -------------------------------------------------------------------------- */

export const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'mr', label: 'मराठी' },
  { code: 'gu', label: 'ગુજરાતી' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'te', label: 'తెలుగు' },
];

const en = {
  'nav.home': 'Home',
  'nav.why': 'Why Napho',
  'nav.pulse': 'Market Pulse',
  'nav.ask': 'Ask Napho',
  'nav.join': 'Join',
  'nav.menu': 'Menu',
  'nav.appearance': 'Appearance',
  'nav.language': 'Language',
  'cta.earlyAccess': 'Get early access',
  'cta.join': 'Join the waitlist',
  'cta.subscribe': 'Subscribe',
  'cta.readPulse': "Read today's Pulse",
  'cta.readMore': 'Read more',
  'cta.viewAll': 'View all briefs',

  'hero.pill': 'Pre-launch · SEBI RIA route',
  'hero.title.a': 'Institutional-grade wealth advice, made ',
  'hero.title.em': 'personal',
  'hero.title.b': ' and accessible to every investor.',
  'hero.lead': 'Too much noise. Too many conflicted opinions. Napho is a trusted advisory layer that explains every recommendation in plain language.',
  'hero.email': 'you@email.com',
  'hero.trustline': 'No spam. Just clarity as we build - unsubscribe anytime.',

  'pulse.eyebrow': 'Market Pulse',
  'pulse.title': "Today's market, in plain language",
  'pulse.lead': 'A calm, daily read on what actually moved - and what it means if you invest for years, not days.',
  'pulse.latest': 'Latest brief',
  'pulse.past': 'Past briefs',
  'pulse.empty': 'The first Market Pulse is on its way.',

  'trust.eyebrow': 'Why people trust Napho',
  'trust.title': 'Trust, built into the structure',
  'trust.1.title': 'Fiduciary by structure',
  'trust.2.title': 'Explainable by design',
  'trust.3.title': 'Personal to your goals',

  'news.title': 'Get the Daily Market Brief',
  'news.lead': 'One clear email each morning. What moved, why, and what it means for a long-term investor.',

  'ask.eyebrow': 'Ask Napho',
  'ask.title': 'Built with our users, in the open',
  'ask.lead': "This isn't a static FAQ. Ask a real question, and join the WhatsApp community where we discuss pain points openly while we build. Questions are welcome - you shape the product.",
  'ask.placeholder': 'What would you like to ask?',
  'ask.submit': 'Send question',
  'ask.whatsapp': 'Join the WhatsApp community',
  'ask.examples': 'What people are asking',

  'why.eyebrow': 'Why Napho',
  'why.title': 'Clarity-first, by design',
  'vision.title': 'Our vision',
  'vision.body': 'We envision a future where every individual has access to the kind of personalized financial guidance that was once available only to the wealthy. By combining AI with deep financial intelligence, we deliver recommendations that are transparent, explainable, and tailored to you.',
  'problem.title': 'The problem',
  'problem.body': 'Retail investors face information overload, conflicted commission-driven advice, and finfluencer noise - with no trustworthy, clarity-first guidance layer.',

  'how.eyebrow': 'How it works',
  'how.title': 'Clarity, in four steps',
  'how.lead': 'From your goals to confident action - and staying oriented long after.',
  'how.1.title': 'Tell us about you',
  'how.1.body': 'Your goals, your timeline, your comfort with risk. No jargon and no judgement - just an honest picture of where you are.',
  'how.2.title': 'Get a plan you understand',
  'how.2.body': 'Napho turns it into clear, personalised guidance - and every recommendation comes with the reasoning behind it, in plain language.',
  'how.3.title': 'Act on the fiduciary route',
  'how.3.body': 'Advice delivered on the SEBI Registered Investment Adviser route - answerable to your interest, never to a commission.',
  'how.4.title': 'Stay oriented, daily',
  'how.4.body': 'The daily Market Pulse keeps you calm and informed - signal over noise, so you always know what actually matters.',

  'who.eyebrow': "Who it's for",
  'who.title': 'Built for every kind of investor',
  'who.lead': 'Different starting points, one need: trustworthy guidance in language that respects you.',
  'who.1.title': 'The first-time investor',
  'who.1.body': 'You want to start, but every app assumes you already speak the language. We explain, in yours.',
  'who.2.title': 'Drowning in noise',
  'who.2.body': 'Too many opinions, tips and finfluencers. You want one calm, trustworthy read you can act on.',
  'who.3.title': 'The busy professional',
  'who.3.body': 'You have the income but not the hours. You want a plan that fits your goals without a second job managing it.',
  'who.4.title': 'Burned before',
  'who.4.body': "You've been sold products that paid the seller, not you. You want advice that is legally on your side.",

  'get.eyebrow': 'What you get',
  'get.title': 'Clarity you can hold in your hand',
  'get.1': 'A plan shaped to your goals, horizon and risk - not a generic model portfolio.',
  'get.2': 'The reasoning behind every recommendation, in plain language.',
  'get.3': 'Advice on the fiduciary SEBI RIA route - answerable only to you.',
  'get.4': 'A calm daily Market Pulse, in your own language.',

  'team.eyebrow': 'Know the team',
  'team.title': 'The people building Napho',

  'join.title': 'Join the Napho waitlist',
  'join.lead': 'Be first in. Get the daily market brief, and help shape a product built in the open.',
  'join.benefit1': 'Early access before public launch',
  'join.benefit2': 'The daily Market Pulse in your inbox',
  'join.benefit3': 'A direct line to influence the build',

  'form.thanksTitle': "You're on the list.",
  'form.thanksBody': "We'll write to you as we build.",
  'share.label': 'Share',
  'share.copied': 'Link copied',

  'footer.tagline': 'Institutional-grade wealth advice, made personal and accessible to every investor.',
  'footer.product': 'Product',
  'footer.company': 'Company',
  'footer.legal': 'Legal',
  'footer.contact': 'Contact',
  'footer.apps': 'Apps coming soon',
  'footer.rights': 'All rights reserved.',
  'footer.entity': 'Napho Private Limited (SEBI RIA licence pending) · Quant LLP (MFD/AMFI ARN).',

  'legal.pending': 'This document is being finalised. Final text will be published on sign-off. Napho is pre-launch and nothing here constitutes advice.',
  'disclaimer.short': 'Napho is pre-launch. Market Pulse is for information and education only - not investment advice.',
};

// ── Hindi - complete ────────────────────────────────────────────────────────
const hi = {
  'nav.home': 'होम',
  'nav.why': 'Napho क्यों',
  'nav.pulse': 'मार्केट पल्स',
  'nav.ask': 'Napho से पूछें',
  'nav.join': 'जुड़ें',
  'cta.earlyAccess': 'जल्दी पहुँच पाएँ',
  'cta.join': 'वेटलिस्ट में शामिल हों',
  'cta.subscribe': 'सब्सक्राइब करें',
  'cta.readPulse': 'आज का पल्स पढ़ें',
  'cta.readMore': 'और पढ़ें',
  'cta.viewAll': 'सभी ब्रीफ़ देखें',
  'hero.pill': 'लॉन्च से पहले · SEBI RIA मार्ग',
  'hero.title.a': 'संस्थागत-स्तर की धन सलाह, अब ',
  'hero.title.em': 'व्यक्तिगत',
  'hero.title.b': ' और हर निवेशक के लिए सुलभ।',
  'hero.lead': 'बहुत शोर। बहुत सी परस्पर विरोधी राय। Napho एक भरोसेमंद सलाह परत है जो हर सिफ़ारिश को सरल भाषा में समझाती है।',
  'hero.email': 'you@email.com',
  'hero.trustline': 'कोई स्पैम नहीं। बस स्पष्टता, जैसे-जैसे हम आगे बढ़ते हैं - कभी भी अनसब्सक्राइब करें।',
  'pulse.eyebrow': 'मार्केट पल्स',
  'pulse.title': 'आज का बाज़ार, सरल भाषा में',
  'pulse.lead': 'एक शांत, रोज़ाना का विश्लेषण - असल में क्या बदला, और वर्षों के लिए निवेश करने वालों के लिए इसका क्या मतलब है।',
  'pulse.latest': 'ताज़ा ब्रीफ़',
  'pulse.past': 'पिछले ब्रीफ़',
  'pulse.empty': 'पहला मार्केट पल्स जल्द ही आ रहा है।',
  'trust.eyebrow': 'लोग Napho पर भरोसा क्यों करते हैं',
  'trust.title': 'भरोसा, संरचना में निहित',
  'trust.1.title': 'संरचना से फ़िड्यूशरी',
  'trust.2.title': 'डिज़ाइन से समझने योग्य',
  'trust.3.title': 'आपके लक्ष्यों के अनुरूप',
  'news.title': 'रोज़ाना मार्केट ब्रीफ़ पाएँ',
  'news.lead': 'हर सुबह एक स्पष्ट ईमेल। क्या बदला, क्यों, और दीर्घकालिक निवेशक के लिए इसका क्या मतलब है।',
  'ask.eyebrow': 'Napho से पूछें',
  'ask.title': 'अपने उपयोगकर्ताओं के साथ, खुलेपन में निर्मित',
  'ask.lead': 'यह कोई स्थिर FAQ नहीं है। एक असली सवाल पूछें, और WhatsApp समुदाय से जुड़ें जहाँ हम निर्माण के दौरान समस्याओं पर खुलकर चर्चा करते हैं। सवालों का स्वागत है - आप उत्पाद को आकार देते हैं।',
  'ask.placeholder': 'आप क्या पूछना चाहेंगे?',
  'ask.submit': 'सवाल भेजें',
  'ask.whatsapp': 'WhatsApp समुदाय से जुड़ें',
  'ask.examples': 'लोग क्या पूछ रहे हैं',
  'why.eyebrow': 'Napho क्यों',
  'why.title': 'स्पष्टता-पहले, डिज़ाइन से',
  'vision.title': 'हमारी दृष्टि',
  'vision.body': 'हम एक ऐसे भविष्य की कल्पना करते हैं जहाँ हर व्यक्ति को वैसी व्यक्तिगत वित्तीय सलाह मिले जो कभी केवल संपन्न लोगों तक सीमित थी। AI को गहरी वित्तीय समझ के साथ जोड़कर, हम ऐसी सिफ़ारिशें देते हैं जो पारदर्शी, समझाने योग्य और आपके लिए अनुरूप हों।',
  'problem.title': 'समस्या',
  'problem.body': 'खुदरा निवेशक सूचना की अधिकता, कमीशन-आधारित पक्षपाती सलाह और finfluencer के शोर का सामना करते हैं - और कोई भरोसेमंद, स्पष्टता-पहले मार्गदर्शन परत नहीं है।',
  'how.eyebrow': 'यह कैसे काम करता है',
  'how.title': 'स्पष्टता, चार चरणों में',
  'how.lead': 'आपके लक्ष्यों से आत्मविश्वासपूर्ण कार्रवाई तक - और उसके बाद भी दिशा में बने रहना।',
  'how.1.title': 'हमें अपने बारे में बताएँ',
  'how.1.body': 'आपके लक्ष्य, आपकी समय-सीमा, जोखिम के प्रति आपकी सहजता। कोई शब्दजाल नहीं, कोई निर्णय नहीं - बस एक ईमानदार तस्वीर कि आप कहाँ हैं।',
  'how.2.title': 'ऐसी योजना पाएँ जिसे आप समझें',
  'how.2.body': 'Napho इसे स्पष्ट, व्यक्तिगत मार्गदर्शन में बदलता है - और हर सिफ़ारिश के साथ उसका कारण, सरल भाषा में।',
  'how.3.title': 'फ़िड्यूशरी मार्ग पर कार्य करें',
  'how.3.body': 'सलाह SEBI पंजीकृत निवेश सलाहकार मार्ग पर दी जाती है - जो आपके हित के प्रति उत्तरदायी है, कभी कमीशन के प्रति नहीं।',
  'how.4.title': 'रोज़ाना दिशा में बने रहें',
  'how.4.body': 'रोज़ाना मार्केट पल्स आपको शांत और सूचित रखता है - शोर पर संकेत, ताकि आप हमेशा जानें कि वास्तव में क्या मायने रखता है।',
  'who.eyebrow': 'यह किसके लिए है',
  'who.title': 'हर तरह के निवेशक के लिए बनाया गया',
  'who.lead': 'अलग-अलग शुरुआती बिंदु, एक ज़रूरत: ऐसी भाषा में भरोसेमंद मार्गदर्शन जो आपका सम्मान करे।',
  'who.1.title': 'पहली बार का निवेशक',
  'who.1.body': 'आप शुरू करना चाहते हैं, पर हर ऐप मानता है कि आप पहले से भाषा जानते हैं। हम समझाते हैं, आपकी भाषा में।',
  'who.2.title': 'शोर में डूबे हुए',
  'who.2.body': 'बहुत सारी राय, टिप्स और finfluencer। आप एक शांत, भरोसेमंद विश्लेषण चाहते हैं जिस पर अमल किया जा सके।',
  'who.3.title': 'व्यस्त पेशेवर',
  'who.3.body': 'आपके पास आय है पर समय नहीं। आप ऐसी योजना चाहते हैं जो आपके लक्ष्यों के अनुरूप हो, बिना उसे संभालने की दूसरी नौकरी के।',
  'who.4.title': 'पहले ठगे गए',
  'who.4.body': 'आपको ऐसे उत्पाद बेचे गए जिनसे विक्रेता को फ़ायदा हुआ, आपको नहीं। आप ऐसी सलाह चाहते हैं जो कानूनन आपके पक्ष में हो।',
  'get.eyebrow': 'आपको क्या मिलता है',
  'get.title': 'स्पष्टता जिसे आप थाम सकें',
  'get.1': 'आपके लक्ष्यों, अवधि और जोखिम के अनुरूप योजना - कोई सामान्य मॉडल पोर्टफोलियो नहीं।',
  'get.2': 'हर सिफ़ारिश के पीछे का कारण, सरल भाषा में।',
  'get.3': 'फ़िड्यूशरी SEBI RIA मार्ग पर सलाह - केवल आपके प्रति उत्तरदायी।',
  'get.4': 'एक शांत रोज़ाना मार्केट पल्स, आपकी अपनी भाषा में।',
  'team.eyebrow': 'टीम को जानें',
  'team.title': 'Napho बनाने वाले लोग',
  'join.title': 'Napho वेटलिस्ट में शामिल हों',
  'join.lead': 'सबसे पहले अंदर आएँ। रोज़ाना मार्केट ब्रीफ़ पाएँ, और खुलेपन में बन रहे उत्पाद को आकार दें।',
  'join.benefit1': 'सार्वजनिक लॉन्च से पहले जल्दी पहुँच',
  'join.benefit2': 'आपके इनबॉक्स में रोज़ाना मार्केट पल्स',
  'join.benefit3': 'निर्माण को प्रभावित करने का सीधा माध्यम',
  'form.thanksTitle': 'आप सूची में हैं।',
  'form.thanksBody': 'जैसे-जैसे हम बनाते हैं, हम आपको लिखेंगे।',
  'share.label': 'साझा करें',
  'share.copied': 'लिंक कॉपी हुआ',
  'footer.tagline': 'संस्थागत-स्तर की धन सलाह, हर निवेशक के लिए व्यक्तिगत और सुलभ।',
  'footer.product': 'उत्पाद',
  'footer.company': 'कंपनी',
  'footer.legal': 'कानूनी',
  'footer.contact': 'संपर्क',
  'footer.apps': 'ऐप्स जल्द आ रहे हैं',
  'footer.rights': 'सर्वाधिकार सुरक्षित।',
  'footer.entity': 'Napho Private Limited (SEBI RIA लाइसेंस लंबित) · Quant LLP (MFD/AMFI ARN)।',
  'legal.pending': 'यह दस्तावेज़ अंतिम रूप ले रहा है। अंतिम पाठ स्वीकृति पर प्रकाशित किया जाएगा। Napho लॉन्च से पहले है और यहाँ कुछ भी सलाह नहीं है।',
  'disclaimer.short': 'Napho लॉन्च से पहले है। मार्केट पल्स केवल जानकारी और शिक्षा के लिए है - निवेश सलाह नहीं।',
};

// ── Marathi - complete ──────────────────────────────────────────────────────
const mr = {
  'nav.home': 'होम',
  'nav.why': 'Napho का',
  'nav.pulse': 'मार्केट पल्स',
  'nav.ask': 'Napho ला विचारा',
  'nav.join': 'सामील व्हा',
  'cta.earlyAccess': 'लवकर प्रवेश मिळवा',
  'cta.join': 'वेटलिस्टमध्ये सामील व्हा',
  'cta.subscribe': 'सबस्क्राइब करा',
  'cta.readPulse': 'आजचा पल्स वाचा',
  'cta.readMore': 'अधिक वाचा',
  'cta.viewAll': 'सर्व ब्रीफ पाहा',
  'hero.pill': 'लॉन्चपूर्व · SEBI RIA मार्ग',
  'hero.title.a': 'संस्थात्मक दर्जाचा संपत्ती सल्ला, आता ',
  'hero.title.em': 'वैयक्तिक',
  'hero.title.b': ' आणि प्रत्येक गुंतवणूकदारासाठी सुलभ.',
  'hero.lead': 'खूप गोंधळ. खूप परस्परविरोधी मते. Napho एक विश्वासार्ह सल्ला स्तर आहे जो प्रत्येक शिफारस सोप्या भाषेत समजावतो.',
  'hero.email': 'you@email.com',
  'hero.trustline': 'स्पॅम नाही. फक्त स्पष्टता, आम्ही पुढे जात असताना - कधीही अनसब्सक्राइब करा.',
  'pulse.eyebrow': 'मार्केट पल्स',
  'pulse.title': 'आजचा बाजार, सोप्या भाषेत',
  'pulse.lead': 'एक शांत, रोजचे विश्लेषण - प्रत्यक्षात काय बदलले, आणि वर्षांसाठी गुंतवणूक करणाऱ्यांसाठी त्याचा काय अर्थ आहे.',
  'pulse.latest': 'ताजा ब्रीफ',
  'pulse.past': 'मागील ब्रीफ',
  'pulse.empty': 'पहिला मार्केट पल्स लवकरच येत आहे.',
  'trust.eyebrow': 'लोक Napho वर विश्वास का ठेवतात',
  'trust.title': 'विश्वास, रचनेतच अंतर्भूत',
  'trust.1.title': 'रचनेने फिड्युशरी',
  'trust.2.title': 'रचनेने समजण्याजोगे',
  'trust.3.title': 'तुमच्या ध्येयांनुसार वैयक्तिक',
  'news.title': 'रोजचा मार्केट ब्रीफ मिळवा',
  'news.lead': 'दररोज सकाळी एक स्पष्ट ईमेल. काय बदलले, का, आणि दीर्घकालीन गुंतवणूकदारासाठी त्याचा काय अर्थ.',
  'ask.eyebrow': 'Napho ला विचारा',
  'ask.title': 'आमच्या वापरकर्त्यांसोबत, खुलेपणाने घडवलेले',
  'ask.lead': 'हे स्थिर FAQ नाही. खरा प्रश्न विचारा, आणि WhatsApp समुदायात सामील व्हा जिथे आम्ही घडवताना अडचणींवर खुलेपणाने चर्चा करतो. प्रश्नांचे स्वागत आहे - तुम्ही उत्पादनाला आकार देता.',
  'ask.placeholder': 'तुम्हाला काय विचारायचे आहे?',
  'ask.submit': 'प्रश्न पाठवा',
  'ask.whatsapp': 'WhatsApp समुदायात सामील व्हा',
  'ask.examples': 'लोक काय विचारत आहेत',
  'why.eyebrow': 'Napho का',
  'why.title': 'स्पष्टता-प्रथम, रचनेने',
  'vision.title': 'आमचे स्वप्न',
  'vision.body': 'आम्ही अशा भविष्याची कल्पना करतो जिथे प्रत्येक व्यक्तीला असा वैयक्तिक आर्थिक सल्ला मिळेल जो पूर्वी फक्त श्रीमंतांसाठी उपलब्ध होता. AI ला सखोल आर्थिक समजेसह जोडून, आम्ही पारदर्शक, समजावता येणाऱ्या आणि तुमच्यासाठी अनुरूप शिफारसी देतो.',
  'problem.title': 'समस्या',
  'problem.body': 'किरकोळ गुंतवणूकदारांना माहितीचा भडिमार, कमिशन-आधारित पक्षपाती सल्ला आणि finfluencer चा गोंधळ यांचा सामना करावा लागतो - आणि विश्वासार्ह, स्पष्टता-प्रथम मार्गदर्शन स्तर नाही.',
  'how.eyebrow': 'हे कसे कार्य करते',
  'how.title': 'स्पष्टता, चार टप्प्यांत',
  'how.lead': 'तुमच्या ध्येयांपासून आत्मविश्वासपूर्ण कृतीपर्यंत - आणि नंतरही दिशेत राहणे.',
  'how.1.title': 'आम्हाला तुमच्याबद्दल सांगा',
  'how.1.body': 'तुमची ध्येये, तुमची कालमर्यादा, जोखमीबाबतची तुमची सहजता. शब्दजंजाळ नाही, निवाडा नाही - फक्त तुम्ही कुठे आहात याचे प्रामाणिक चित्र.',
  'how.2.title': 'तुम्हाला समजेल अशी योजना मिळवा',
  'how.2.body': 'Napho त्याचे स्पष्ट, वैयक्तिक मार्गदर्शनात रूपांतर करते - आणि प्रत्येक शिफारशीमागील कारण, सोप्या भाषेत.',
  'how.3.title': 'फिड्युशरी मार्गावर कृती करा',
  'how.3.body': 'सल्ला SEBI नोंदणीकृत गुंतवणूक सल्लागार मार्गावर दिला जातो - तो तुमच्या हिताला उत्तरदायी आहे, कधीही कमिशनला नाही.',
  'how.4.title': 'दररोज दिशेत राहा',
  'how.4.body': 'रोजचा मार्केट पल्स तुम्हाला शांत आणि माहितीपूर्ण ठेवतो - गोंधळावर संकेत, जेणेकरून खरोखर काय महत्त्वाचे आहे हे तुम्हाला नेहमी कळेल.',
  'who.eyebrow': 'हे कोणासाठी आहे',
  'who.title': 'प्रत्येक प्रकारच्या गुंतवणूकदारासाठी बनवलेले',
  'who.lead': 'वेगवेगळे सुरुवातीचे बिंदू, एक गरज: तुमचा आदर करणाऱ्या भाषेत विश्वासार्ह मार्गदर्शन.',
  'who.1.title': 'पहिल्यांदाचा गुंतवणूकदार',
  'who.1.body': 'तुम्हाला सुरुवात करायची आहे, पण प्रत्येक अ‍ॅप गृहीत धरते की तुम्हाला आधीच भाषा येते. आम्ही समजावतो, तुमच्या भाषेत.',
  'who.2.title': 'गोंधळात बुडालेले',
  'who.2.body': 'खूप मते, टिप्स आणि finfluencer. तुम्हाला एक शांत, विश्वासार्ह विश्लेषण हवे आहे ज्यावर कृती करता येईल.',
  'who.3.title': 'व्यस्त व्यावसायिक',
  'who.3.body': 'तुमच्याकडे उत्पन्न आहे पण वेळ नाही. तुम्हाला अशी योजना हवी आहे जी तुमच्या ध्येयांना बसते, ती सांभाळण्याच्या दुसऱ्या नोकरीशिवाय.',
  'who.4.title': 'आधी फसवले गेलेले',
  'who.4.body': 'तुम्हाला अशी उत्पादने विकली गेली ज्यातून विक्रेत्याला फायदा झाला, तुम्हाला नाही. तुम्हाला असा सल्ला हवा आहे जो कायदेशीररीत्या तुमच्या बाजूने आहे.',
  'get.eyebrow': 'तुम्हाला काय मिळते',
  'get.title': 'हातात धरता येईल अशी स्पष्टता',
  'get.1': 'तुमच्या ध्येयांनुसार, कालावधीनुसार आणि जोखमीनुसार योजना - सामान्य मॉडेल पोर्टफोलिओ नाही.',
  'get.2': 'प्रत्येक शिफारशीमागील कारण, सोप्या भाषेत.',
  'get.3': 'फिड्युशरी SEBI RIA मार्गावर सल्ला - फक्त तुम्हाला उत्तरदायी.',
  'get.4': 'एक शांत रोजचा मार्केट पल्स, तुमच्या स्वतःच्या भाषेत.',
  'team.eyebrow': 'टीमला जाणून घ्या',
  'team.title': 'Napho घडवणारी माणसे',
  'join.title': 'Napho वेटलिस्टमध्ये सामील व्हा',
  'join.lead': 'सर्वात आधी आत या. रोजचा मार्केट ब्रीफ मिळवा, आणि खुलेपणाने घडणाऱ्या उत्पादनाला आकार द्या.',
  'join.benefit1': 'सार्वजनिक लॉन्चपूर्वी लवकर प्रवेश',
  'join.benefit2': 'तुमच्या इनबॉक्समध्ये रोजचा मार्केट पल्स',
  'join.benefit3': 'घडणीवर प्रभाव टाकण्याचा थेट मार्ग',
  'form.thanksTitle': 'तुम्ही यादीत आहात.',
  'form.thanksBody': 'आम्ही तयार करत असताना तुम्हाला कळवू.',
  'share.label': 'शेअर करा',
  'share.copied': 'लिंक कॉपी झाली',
  'footer.tagline': 'संस्थात्मक दर्जाचा संपत्ती सल्ला, प्रत्येक गुंतवणूकदारासाठी वैयक्तिक आणि सुलभ.',
  'footer.product': 'उत्पादन',
  'footer.company': 'कंपनी',
  'footer.legal': 'कायदेशीर',
  'footer.contact': 'संपर्क',
  'footer.apps': 'अ‍ॅप्स लवकरच येत आहेत',
  'footer.rights': 'सर्व हक्क राखीव.',
  'footer.entity': 'Napho Private Limited (SEBI RIA परवाना प्रलंबित) · Quant LLP (MFD/AMFI ARN).',
  'legal.pending': 'हा दस्तऐवज अंतिम केला जात आहे. अंतिम मजकूर मंजुरीनंतर प्रकाशित होईल. Napho लॉन्चपूर्व आहे आणि येथे काहीही सल्ला नाही.',
  'disclaimer.short': 'Napho लॉन्चपूर्व आहे. मार्केट पल्स फक्त माहिती आणि शिक्षणासाठी आहे - गुंतवणूक सल्ला नाही.',
};

// ── Gujarati — complete ──────────────────────────────────────────────────────
const gu = {
  'nav.home': 'હોમ',
  'nav.why': 'Napho શા માટે',
  'nav.pulse': 'માર્કેટ પલ્સ',
  'nav.ask': 'Napho ને પૂછો',
  'nav.join': 'જોડાઓ',
  'nav.menu': 'મેનૂ',
  'nav.appearance': 'દેખાવ',
  'nav.language': 'ભાષા',
  'cta.earlyAccess': 'વહેલો પ્રવેશ મેળવો',
  'cta.join': 'વેઇટલિસ્ટમાં જોડાઓ',
  'cta.subscribe': 'સબ્સ્ક્રાઇબ કરો',
  'cta.readPulse': 'આજનું પલ્સ વાંચો',
  'cta.readMore': 'વધુ વાંચો',
  'cta.viewAll': 'બધા બ્રીફ જુઓ',
  'hero.pill': 'લોન્ચ પહેલાં · SEBI RIA માર્ગ',
  'hero.title.a': 'સંસ્થાકીય-સ્તરની સંપત્તિ સલાહ, હવે ',
  'hero.title.em': 'વ્યક્તિગત',
  'hero.title.b': ' અને દરેક રોકાણકાર માટે સુલભ.',
  'hero.lead': 'ઘણો ઘોંઘાટ. ઘણા વિરોધાભાસી મંતવ્યો. Napho એક વિશ્વસનીય સલાહ સ્તર છે જે દરેક ભલામણ સરળ ભાષામાં સમજાવે છે.',
  'hero.email': 'you@email.com',
  'hero.trustline': 'સ્પામ નહીં. ફક્ત સ્પષ્ટતા, અમે આગળ વધીએ છીએ ત્યારે — ગમે ત્યારે અનસબ્સ્ક્રાઇબ કરો.',
  'pulse.eyebrow': 'માર્કેટ પલ્સ',
  'pulse.title': 'આજનું બજાર, સરળ ભાષામાં',
  'pulse.lead': 'એક શાંત, રોજનું વિશ્લેષણ — ખરેખર શું બદલાયું, અને વર્ષો માટે રોકાણ કરનારાઓ માટે તેનો શું અર્થ છે.',
  'pulse.latest': 'તાજું બ્રીફ',
  'pulse.past': 'અગાઉનાં બ્રીફ',
  'pulse.empty': 'પહેલું માર્કેટ પલ્સ ટૂંક સમયમાં આવી રહ્યું છે.',
  'trust.eyebrow': 'લોકો Napho પર શા માટે વિશ્વાસ કરે છે',
  'trust.title': 'વિશ્વાસ, રચનામાં જ સમાવિષ્ટ',
  'trust.1.title': 'રચનાથી ફિડ્યુશરી',
  'trust.2.title': 'ડિઝાઇનથી સમજાવી શકાય તેવું',
  'trust.3.title': 'તમારાં લક્ષ્યો માટે વ્યક્તિગત',
  'news.title': 'રોજનું માર્કેટ બ્રીફ મેળવો',
  'news.lead': 'દરરોજ સવારે એક સ્પષ્ટ ઈમેલ. શું બદલાયું, શા માટે, અને લાંબા ગાળાના રોકાણકાર માટે તેનો શું અર્થ.',
  'ask.eyebrow': 'Napho ને પૂછો',
  'ask.title': 'અમારા વપરાશકર્તાઓ સાથે, ખુલ્લેઆમ બનાવેલું',
  'ask.lead': 'આ સ્થિર FAQ નથી. ખરો પ્રશ્ન પૂછો, અને WhatsApp સમુદાયમાં જોડાઓ જ્યાં અમે બનાવતી વખતે મુશ્કેલીઓ પર ખુલ્લેઆમ ચર્ચા કરીએ છીએ. પ્રશ્નોનું સ્વાગત છે — તમે ઉત્પાદનને આકાર આપો છો.',
  'ask.placeholder': 'તમે શું પૂછવા માંગો છો?',
  'ask.submit': 'પ્રશ્ન મોકલો',
  'ask.whatsapp': 'WhatsApp સમુદાયમાં જોડાઓ',
  'ask.examples': 'લોકો શું પૂછી રહ્યા છે',
  'why.eyebrow': 'Napho શા માટે',
  'why.title': 'સ્પષ્ટતા-પ્રથમ, ડિઝાઇનથી',
  'vision.title': 'અમારું સ્વપ્ન',
  'vision.body': 'અમે એવા ભવિષ્યની કલ્પના કરીએ છીએ જ્યાં દરેક વ્યક્તિને એવી વ્યક્તિગત નાણાકીય માર્ગદર્શન મળે જે પહેલાં ફક્ત ધનવાનો માટે ઉપલબ્ધ હતી. AI ને ઊંડી નાણાકીય સમજ સાથે જોડીને, અમે પારદર્શક, સમજાવી શકાય તેવી અને તમારા માટે અનુરૂપ ભલામણો આપીએ છીએ.',
  'problem.title': 'સમસ્યા',
  'problem.body': 'છૂટક રોકાણકારોને માહિતીનો અતિરેક, કમિશન-આધારિત પક્ષપાતી સલાહ અને finfluencer નો ઘોંઘાટ સામનો કરવો પડે છે — અને કોઈ વિશ્વસનીય, સ્પષ્ટતા-પ્રથમ માર્ગદર્શન સ્તર નથી.',
  'how.eyebrow': 'આ કેવી રીતે કામ કરે છે',
  'how.title': 'સ્પષ્ટતા, ચાર પગલાંમાં',
  'how.lead': 'તમારાં લક્ષ્યોથી આત્મવિશ્વાસપૂર્ણ ક્રિયા સુધી — અને પછી પણ દિશામાં રહેવું.',
  'how.1.title': 'અમને તમારા વિશે જણાવો',
  'how.1.body': 'તમારાં લક્ષ્યો, તમારી સમયમર્યાદા, જોખમ સાથેની તમારી સહજતા. કોઈ શબ્દજાળ નહીં અને કોઈ ચુકાદો નહીં — ફક્ત તમે ક્યાં છો તેનું પ્રામાણિક ચિત્ર.',
  'how.2.title': 'તમને સમજાય એવી યોજના મેળવો',
  'how.2.body': 'Napho તેને સ્પષ્ટ, વ્યક્તિગત માર્ગદર્શનમાં ફેરવે છે — અને દરેક ભલામણ પાછળનું કારણ, સરળ ભાષામાં.',
  'how.3.title': 'ફિડ્યુશરી માર્ગ પર ક્રિયા કરો',
  'how.3.body': 'સલાહ SEBI રજિસ્ટર્ડ ઇન્વેસ્ટમેન્ટ એડવાઇઝર માર્ગ પર આપવામાં આવે છે — તમારા હિતને જવાબદાર, ક્યારેય કમિશનને નહીં.',
  'how.4.title': 'દરરોજ દિશામાં રહો',
  'how.4.body': 'રોજનું માર્કેટ પલ્સ તમને શાંત અને માહિતગાર રાખે છે — ઘોંઘાટ પર સંકેત, જેથી ખરેખર શું મહત્ત્વનું છે તે તમને હંમેશા ખબર રહે.',
  'who.eyebrow': 'આ કોના માટે છે',
  'who.title': 'દરેક પ્રકારના રોકાણકાર માટે બનાવેલું',
  'who.lead': 'અલગ-અલગ શરૂઆતના બિંદુઓ, એક જરૂરિયાત: તમારો આદર કરતી ભાષામાં વિશ્વસનીય માર્ગદર્શન.',
  'who.1.title': 'પ્રથમ વખતનો રોકાણકાર',
  'who.1.body': 'તમે શરૂ કરવા માંગો છો, પણ દરેક એપ ધારી લે છે કે તમને પહેલેથી ભાષા આવડે છે. અમે સમજાવીએ છીએ, તમારી ભાષામાં.',
  'who.2.title': 'ઘોંઘાટમાં ડૂબેલા',
  'who.2.body': 'ઘણાં બધાં મંતવ્યો, ટિપ્સ અને finfluencer. તમને એક શાંત, વિશ્વસનીય વિશ્લેષણ જોઈએ છે જેના પર તમે ક્રિયા કરી શકો.',
  'who.3.title': 'વ્યસ્ત વ્યાવસાયિક',
  'who.3.body': 'તમારી પાસે આવક છે પણ સમય નથી. તમને એવી યોજના જોઈએ છે જે તમારાં લક્ષ્યોને બંધબેસે, તેને સંભાળવાની બીજી નોકરી વગર.',
  'who.4.title': 'પહેલાં છેતરાયેલા',
  'who.4.body': 'તમને એવાં ઉત્પાદનો વેચવામાં આવ્યાં જેનાથી વેચનારને ફાયદો થયો, તમને નહીં. તમને એવી સલાહ જોઈએ છે જે કાયદેસર રીતે તમારા પક્ષે હોય.',
  'get.eyebrow': 'તમને શું મળે છે',
  'get.title': 'હાથમાં પકડી શકાય એવી સ્પષ્ટતા',
  'get.1': 'તમારાં લક્ષ્યો, સમયગાળા અને જોખમ પ્રમાણેની યોજના — સામાન્ય મોડેલ પોર્ટફોલિયો નહીં.',
  'get.2': 'દરેક ભલામણ પાછળનું કારણ, સરળ ભાષામાં.',
  'get.3': 'ફિડ્યુશરી SEBI RIA માર્ગ પર સલાહ — ફક્ત તમને જવાબદાર.',
  'get.4': 'એક શાંત રોજનું માર્કેટ પલ્સ, તમારી પોતાની ભાષામાં.',
  'team.eyebrow': 'ટીમને જાણો',
  'team.title': 'Napho બનાવતા લોકો',
  'join.title': 'Napho વેઇટલિસ્ટમાં જોડાઓ',
  'join.lead': 'સૌથી પહેલાં અંદર આવો. રોજનું માર્કેટ બ્રીફ મેળવો, અને ખુલ્લેઆમ બનતા ઉત્પાદનને આકાર આપો.',
  'join.benefit1': 'જાહેર લોન્ચ પહેલાં વહેલો પ્રવેશ',
  'join.benefit2': 'તમારા ઇનબોક્સમાં રોજનું માર્કેટ પલ્સ',
  'join.benefit3': 'બનાવટ પર પ્રભાવ પાડવાનો સીધો માર્ગ',
  'form.thanksTitle': 'તમે યાદીમાં છો.',
  'form.thanksBody': 'અમે તૈયાર કરીએ છીએ ત્યારે તમને લખીશું.',
  'share.label': 'શેર કરો',
  'share.copied': 'લિંક કૉપિ થઈ',
  'footer.tagline': 'સંસ્થાકીય-સ્તરની સંપત્તિ સલાહ, દરેક રોકાણકાર માટે વ્યક્તિગત અને સુલભ.',
  'footer.product': 'ઉત્પાદન',
  'footer.company': 'કંપની',
  'footer.legal': 'કાયદેસર',
  'footer.contact': 'સંપર્ક',
  'footer.apps': 'એપ્સ ટૂંક સમયમાં આવી રહી છે',
  'footer.rights': 'સર્વ હક્ક અનામત.',
  'footer.entity': 'Napho Private Limited (SEBI RIA લાઇસન્સ બાકી) · Quant LLP (MFD/AMFI ARN).',
  'legal.pending': 'આ દસ્તાવેજ અંતિમ સ્વરૂપ લઈ રહ્યો છે. અંતિમ લખાણ મંજૂરી પર પ્રકાશિત કરવામાં આવશે. Napho લોન્ચ પહેલાં છે અને અહીં કંઈપણ સલાહ નથી.',
  'disclaimer.short': 'Napho લોન્ચ પહેલાં છે. માર્કેટ પલ્સ ફક્ત માહિતી અને શિક્ષણ માટે છે — રોકાણ સલાહ નહીં.',
};

// ── Tamil / Bengali / Telugu - starter coverage (nav + hero + key) ────────────
const ta = {
  'nav.home': 'முகப்பு', 'nav.why': 'Napho ஏன்', 'nav.pulse': 'மார்க்கெட் பல்ஸ்',
  'nav.ask': 'Napho விடம் கேளுங்கள்', 'nav.join': 'இணையுங்கள்',
  'cta.join': 'காத்திருப்புப் பட்டியலில் இணையுங்கள்', 'cta.readPulse': 'இன்றைய பல்ஸ் படியுங்கள்', 'cta.viewAll': 'அனைத்து பிரீஃப்களையும் காண்க',
  'hero.pill': 'தொடக்கத்திற்கு முன் · SEBI RIA வழி',
  'hero.title.a': 'நிறுவன தரமான செல்வ ஆலோசனை, இப்போது ', 'hero.title.em': 'தனிப்பட்ட',
  'hero.title.b': ' மற்றும் ஒவ்வொரு முதலீட்டாளருக்கும் அணுகக்கூடியது.',
  'hero.lead': 'மிகுந்த சத்தம். பல முரண்பட்ட கருத்துகள். Napho என்பது ஒவ்வொரு பரிந்துரையையும் எளிய மொழியில் விளக்கும் நம்பகமான ஆலோசனை அடுக்கு.',
  'pulse.title': 'இன்றைய சந்தை, எளிய மொழியில்',
  'trust.title': 'நம்பிக்கை, கட்டமைப்பிலேயே',
  'team.eyebrow': 'குழுவை அறியுங்கள்', 'footer.contact': 'தொடர்பு',
};

const bn = {
  'nav.home': 'হোম', 'nav.why': 'কেন Napho', 'nav.pulse': 'মার্কেট পালস',
  'nav.ask': 'Napho কে জিজ্ঞাসা করুন', 'nav.join': 'যোগ দিন',
  'cta.join': 'ওয়েটলিস্টে যোগ দিন', 'cta.readPulse': 'আজকের পালস পড়ুন', 'cta.viewAll': 'সব ব্রিফ দেখুন',
  'hero.pill': 'লঞ্চের আগে · SEBI RIA পথ',
  'hero.title.a': 'প্রাতিষ্ঠানিক-মানের সম্পদ পরামর্শ, এখন ', 'hero.title.em': 'ব্যক্তিগত',
  'hero.title.b': ' এবং প্রত্যেক বিনিয়োগকারীর জন্য সহজলভ্য.',
  'hero.lead': 'অনেক কোলাহল। অনেক পরস্পরবিরোধী মতামত। Napho একটি বিশ্বাসযোগ্য পরামর্শ স্তর যা প্রতিটি সুপারিশ সহজ ভাষায় ব্যাখ্যা করে।',
  'pulse.title': 'আজকের বাজার, সহজ ভাষায়',
  'trust.title': 'বিশ্বাস, কাঠামোর মধ্যেই নিহিত',
  'team.eyebrow': 'দলকে জানুন', 'footer.contact': 'যোগাযোগ',
};

const te = {
  'nav.home': 'హోమ్', 'nav.why': 'Napho ఎందుకు', 'nav.pulse': 'మార్కెట్ పల్స్',
  'nav.ask': 'Napho ని అడగండి', 'nav.join': 'చేరండి',
  'cta.join': 'వెయిట్‌లిస్ట్‌లో చేరండి', 'cta.readPulse': 'నేటి పల్స్ చదవండి', 'cta.viewAll': 'అన్ని బ్రీఫ్‌లను చూడండి',
  'hero.pill': 'ప్రారంభానికి ముందు · SEBI RIA మార్గం',
  'hero.title.a': 'సంస్థాగత-స్థాయి సంపద సలహా, ఇప్పుడు ', 'hero.title.em': 'వ్యక్తిగతం',
  'hero.title.b': ' మరియు ప్రతి పెట్టుబడిదారునికి అందుబాటులో.',
  'hero.lead': 'చాలా గందరగోళం. చాలా విరుద్ధ అభిప్రాయాలు. Napho అనేది ప్రతి సిఫార్సును సరళ భాషలో వివరించే విశ్వసనీయ సలహా పొర.',
  'pulse.title': 'నేటి మార్కెట్, సరళ భాషలో',
  'trust.title': 'నమ్మకం, నిర్మాణంలోనే',
  'team.eyebrow': 'బృందాన్ని తెలుసుకోండి', 'footer.contact': 'సంప్రదింపు',
};

export const strings = { en, hi, mr, gu, ta, bn, te };

export const FULLY_TRANSLATED = ['en', 'hi', 'mr', 'gu'];

export function translate(lang, key) {
  const table = strings[lang] || {};
  return table[key] || en[key] || key;
}
